<template>
  <div class="capture-selector">
    <div class="selector-header">
      <h2>Recent Captures</h2>
      <button
        class="btn-refresh"
        @click="$emit('refresh')"
        title="Refresh list"
      >
        🔄 Refresh
      </button>
    </div>

    <!-- No branches found -->
    <div v-if="branches.length === 0" class="no-branches">
      <p>No capture branches found for this client.</p>
      <p class="help-text">
        Automated captures will appear here once configured.
      </p>
    </div>

    <!-- Branch list -->
    <div v-else class="branch-list">
      <!-- Pending Review Section -->
      <div v-if="pendingBranches.length > 0" class="branch-section">
        <h3 class="section-title">
          ⚠️ Pending Review ({{ pendingBranches.length }})
        </h3>

        <div
          v-for="branch in pendingBranches"
          :key="branch.name"
          :class="[
            'branch-card',
            'pending',
            { selected: branch.name === selectedBranch },
          ]"
          @click="selectBranch(branch)"
        >
          <div class="branch-header">
            <div class="branch-info">
              <span class="branch-name">{{ branch.name }}</span>
              <span class="branch-date">{{ formatDate(branch.date) }}</span>
            </div>
            <span class="status-badge pending">Pending Review</span>
          </div>

          <div v-if="branch.metadata" class="branch-metadata">
            <span class="metadata-item">
              📊 {{ branch.metadata.changeCount || "?" }} changes
            </span>
            <span class="metadata-item">
              ⚠️ {{ branch.metadata.criticalCount || 0 }} critical
            </span>
            <span class="metadata-item" v-if="branch.metadata.baselineVersion">
              📌 Baseline v{{ branch.metadata.baselineVersion }}
            </span>
          </div>

          <div class="branch-actions">
            <button
              class="btn-primary-small"
              @click.stop="selectBranch(branch)"
            >
              {{ branch.name === selectedBranch ? "Viewing" : "Review" }}
            </button>
          </div>
        </div>
      </div>

      <!-- Merged Section -->
      <div v-if="mergedBranches.length > 0" class="branch-section">
        <h3 class="section-title">
          ✓ Recently Merged ({{ mergedBranches.length }})
        </h3>

        <div
          v-for="branch in mergedBranches.slice(
            0,
            showAllMerged ? undefined : 3
          )"
          :key="branch.name"
          :class="[
            'branch-card',
            'merged',
            { selected: branch.name === selectedBranch },
          ]"
          @click="selectBranch(branch)"
        >
          <div class="branch-header">
            <div class="branch-info">
              <span class="branch-name">{{ branch.name }}</span>
              <span class="branch-date">{{ formatDate(branch.date) }}</span>
            </div>
            <span class="status-badge merged">Merged</span>
          </div>

          <div v-if="branch.mergedBy" class="merge-info">
            Merged by {{ branch.mergedBy }} on
            {{ formatDate(branch.mergedDate) }}
          </div>

          <div class="branch-actions">
            <button
              class="btn-secondary-small"
              @click.stop="selectBranch(branch)"
            >
              View History
            </button>
          </div>
        </div>

        <!-- Show more button -->
        <button
          v-if="mergedBranches.length > 3"
          class="btn-show-more"
          @click="showAllMerged = !showAllMerged"
        >
          {{
            showAllMerged
              ? "Show Less"
              : `Show ${mergedBranches.length - 3} More`
          }}
        </button>
      </div>

      <!-- Archived/Old Section -->
      <div v-if="archivedBranches.length > 0" class="branch-section">
        <details>
          <summary class="section-title">
            📦 Archived ({{ archivedBranches.length }})
          </summary>

          <div
            v-for="branch in archivedBranches"
            :key="branch.name"
            class="branch-card archived"
            @click="selectBranch(branch)"
          >
            <div class="branch-header">
              <div class="branch-info">
                <span class="branch-name">{{ branch.name }}</span>
                <span class="branch-date">{{ formatDate(branch.date) }}</span>
              </div>
              <span class="status-badge archived">Archived</span>
            </div>
          </div>
        </details>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CaptureSelector",

  props: {
    clientCode: {
      type: String,
      required: true,
    },
    branches: {
      type: Array,
      required: true,
    },
    selectedBranch: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      showAllMerged: false,
    };
  },

  computed: {
    pendingBranches() {
      return this.branches
        .filter((b) => !b.merged)
        .sort((a, b) => new Date(b.date) - new Date(a.date));
    },

    mergedBranches() {
      return this.branches
        .filter((b) => b.merged)
        .sort(
          (a, b) =>
            new Date(b.mergedDate || b.date) - new Date(a.mergedDate || a.date)
        );
    },

    archivedBranches() {
      // Branches older than 60 days
      const sixtyDaysAgo = Date.now() - 60 * 24 * 60 * 60 * 1000;
      return this.branches
        .filter((b) => new Date(b.date) < sixtyDaysAgo)
        .sort((a, b) => new Date(b.date) - new Date(a.date));
    },
  },

  methods: {
    selectBranch(branch) {
      this.$emit("branch-selected", branch.name);
    },

    formatDate(dateString) {
      if (!dateString) return "Unknown";

      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      // Relative time for recent dates
      if (diffMins < 60) {
        return `${diffMins} minute${diffMins !== 1 ? "s" : ""} ago`;
      } else if (diffHours < 24) {
        return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
      } else if (diffDays < 7) {
        return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
      }

      // Absolute date for older
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.capture-selector {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.selector-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
}

.selector-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
}

.btn-refresh {
  padding: 8px 16px;
  background: white;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-refresh:hover {
  background: #f8f9fa;
  border-color: #007bff;
}

.no-branches {
  padding: 60px 24px;
  text-align: center;
  color: #666;
}

.no-branches p {
  margin: 0 0 8px 0;
}

.help-text {
  font-size: 14px;
  color: #999;
}

.branch-list {
  padding: 16px 24px 24px;
}

.branch-section {
  margin-bottom: 32px;
}

.branch-section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  margin: 0 0 12px 0;
  letter-spacing: 0.5px;
}

details summary.section-title {
  cursor: pointer;
  user-select: none;
}

details summary.section-title:hover {
  color: #007bff;
}

.branch-card {
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.branch-card:hover {
  border-color: #007bff;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.1);
}

.branch-card.selected {
  border-color: #007bff;
  background: #f0f8ff;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15);
}

.branch-card.pending {
  border-left: 4px solid #ffc107;
}

.branch-card.merged {
  border-left: 4px solid #28a745;
}

.branch-card.archived {
  opacity: 0.7;
  border-left: 4px solid #6c757d;
}

.branch-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.branch-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.branch-name {
  font-weight: 600;
  font-size: 14px;
  color: #1a1a1a;
  font-family: "Courier New", monospace;
}

.branch-date {
  font-size: 12px;
  color: #666;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.pending {
  background: #fff3cd;
  color: #856404;
}

.status-badge.merged {
  background: #d4edda;
  color: #155724;
}

.status-badge.archived {
  background: #e9ecef;
  color: #495057;
}

.branch-metadata {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #666;
}

.metadata-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.merge-info {
  font-size: 12px;
  color: #666;
  margin-bottom: 12px;
  font-style: italic;
}

.branch-actions {
  display: flex;
  gap: 8px;
}

.btn-primary-small,
.btn-secondary-small {
  padding: 6px 16px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary-small {
  background: #007bff;
  color: white;
}

.btn-primary-small:hover {
  background: #0056b3;
}

.btn-secondary-small {
  background: #6c757d;
  color: white;
}

.btn-secondary-small:hover {
  background: #545b62;
}

.btn-show-more {
  width: 100%;
  padding: 10px;
  background: white;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 13px;
  color: #007bff;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 8px;
}

.btn-show-more:hover {
  background: #f8f9fa;
  border-color: #007bff;
}
</style>
